import { useState } from "react";
import useBrowserLayoutEffect from "./useBrowserLayoutEffect";

// Sync changes to these rules with ui/styles/_breakpoints as needed
const rules = {
  portrait: "(orientation: portrait)",
  desktop: "(min-width: 1098px)",
  hover: "(hover)",
  mobile: "(max-width: 680px)",
};

type Rule = keyof typeof rules;

// jsdom and non-browser environments have no matchMedia; those consumers get the unmatched default.
function queryMatches(rule: Rule): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(rules[rule]).matches;
}

export default function useMediaQuery(rule: Rule): boolean {
  // Resolved during the first render rather than in the effect below, so callers that pick a component
  // tree from the result don't mount the unmatched tree and then remount the matched one. That remount
  // is invisible in most callers but destroys and recreates anything stateful underneath.
  const [match, setMatch] = useState(() => queryMatches(rule));

  useBrowserLayoutEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const update = ({ matches }: { matches: boolean }) => setMatch(matches);
    const media = window.matchMedia(rules[rule]);
    update(media);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return match;
}
